import { MechaTasksBlock } from "./mecha/MechaTasksBlock";
import { MechaCommissionBlock } from "./mecha/MechaCommissionBlock";
import { MechaClientBlock } from "./mecha/MechaClientBlock";
import { Badge } from "@/components/ui/badge";

interface MechaResultBlockProps {
  tool: string;
  data: unknown;
  mechaShift?: boolean;
}

/**
 * Dispatcher for Mecha-Shift structured results.
 * Routes to the correct block component based on the handler name.
 */
export function MechaResultBlock({ tool, data, mechaShift }: MechaResultBlockProps) {
  const block = renderBlock(tool, data);

  if (!block) return null;

  return (
    <div className="space-y-1.5">
      {mechaShift && (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/30">
            ⚡ Instant
          </Badge>
        </div>
      )}
      {block}
    </div>
  );
}

function renderBlock(tool: string, data: unknown): React.ReactNode {
  // Task handlers
  if (tool.startsWith("mecha-tasks-")) {
    return <MechaTasksBlock data={data as Parameters<typeof MechaTasksBlock>[0]["data"]} />;
  }

  // Commission handlers
  if (tool.startsWith("mecha-commission-")) {
    return <MechaCommissionBlock data={data as Parameters<typeof MechaCommissionBlock>[0]["data"]} />;
  }

  // Client handlers
  if (tool.startsWith("mecha-client-")) {
    return <MechaClientBlock data={data as Parameters<typeof MechaClientBlock>[0]["data"]} />;
  }

  // Financial handler — reuse a simple formatted view
  if (tool === "mecha-financial-summary") {
    return <MechaFinancialBlock data={data as MechaFinancialData} />;
  }

  return null;
}

// ── Inline financial block (lightweight, not worth a separate file) ──

interface MechaFinancialData {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  profitMargin: number;
  invoiceCount: number;
  period: string;
}

function MechaFinancialBlock({ data }: { data: MechaFinancialData }) {
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  return (
    <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-border/30 bg-muted/30">
        <h3 className="text-sm font-medium text-foreground">Financial Summary — {data.period}</h3>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Revenue</p>
          <p className="text-lg font-semibold text-green-400">{fmt(data.totalRevenue)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Expenses</p>
          <p className="text-lg font-semibold text-red-400">{fmt(data.totalExpenses)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Net Profit</p>
          <p className="text-lg font-semibold text-foreground">{fmt(data.netProfit)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Margin</p>
          <p className="text-lg font-semibold text-foreground">{data.profitMargin.toFixed(1)}%</p>
        </div>
      </div>
      <div className="px-4 pb-3">
        <p className="text-xs text-muted-foreground">{data.invoiceCount} paid invoices</p>
      </div>
    </div>
  );
}
